<template>
  <div class="accommodation-page">
    <!-- Hero Section -->
    <section class="accommodation-hero-section">
      <div class="hero-background">
        <img src="@/assets/images/natural resources/Kilimanjaro Banner.jpg" alt="Luxury Accommodation Tanzania" class="hero-image" />
        <div class="hero-overlay"></div>
      </div>
      <div class="hero-content">
        <div class="container">
          <h1 class="hero-title" data-aos="fade-up">Premium Accommodation</h1>
          <h2 class="hero-subtitle" data-aos="fade-up" data-aos-delay="100">
            Luxury Stays for Africa HR Summit Attendees
          </h2>
          <p class="hero-description" data-aos="fade-up" data-aos-delay="200">
            Experience world-class hospitality with our carefully curated selection of premium hotels in Tanzania
          </p>
        </div>
      </div>
    </section>

    <!-- Hotel Categories Filter -->
    <section class="filter-section">
      <div class="container">
        <div class="filter-controls" data-aos="fade-up">
          <button
            class="filter-btn"
            :class="{ active: activeFilter === 'all' }"
            @click="filterHotels('all')"
          >
            All Hotels
          </button>
          <button
            class="filter-btn"
            :class="{ active: activeFilter === 'luxury' }"
            @click="filterHotels('luxury')"
          >
            Luxury (5★)
          </button>
          <button
            class="filter-btn"
            :class="{ active: activeFilter === 'business' }"
            @click="filterHotels('business')"
          >
            Business (4★)
          </button>
          <button
            class="filter-btn"
            :class="{ active: activeFilter === 'comfort' }"
            @click="filterHotels('comfort')"
          >
            Comfort (3★)
          </button>
        </div>
      </div>
    </section>

    <!-- Hotels Showcase -->
    <section class="hotels-section">
      <div class="container">
        <!-- Section Introduction -->
        <div class="section-intro text-center mb-5" data-aos="fade-up">
          <h3 class="section-title">Our Premium Hotel Partners</h3>
          <p class="section-description">
            Experience exceptional hospitality at these carefully selected accommodations.
            Each hotel offers unique amenities and services to ensure your stay during the
            Africa HR Summit is comfortable, convenient, and memorable.
          </p>
        </div>

        <!-- Hotels Showcase Gallery -->
        <div class="hotels-showcase-gallery">
          <div class="row">
            <div
              v-for="(hotel, index) in filteredHotels"
              :key="hotel.id"
              class="col-lg-6 col-xl-4 mb-4"
              data-aos="fade-up"
              :data-aos-delay="index * 100"
            >
              <div class="hotel-showcase-card">
                <!-- Hotel Image -->
                <div class="hotel-image-header">
                  <img :src="hotel.image" :alt="hotel.name" class="hotel-main-image" />
                  <div class="hotel-category-overlay">
                    <span class="category-badge" :class="hotel.category">{{ hotel.categoryLabel }}</span>
                  </div>
                </div>

                <!-- Hotel Content -->
                <div class="hotel-card-content">
                  <!-- Hotel Title & Location -->
                  <div class="hotel-header">
                    <h3 class="hotel-title">{{ hotel.name }}</h3>
                    <div class="hotel-location">
                      <svg class="location-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                      <span>{{ hotel.location }}</span>
                    </div>
                  </div>

                  <!-- Hotel Description -->
                  <p class="hotel-description">{{ hotel.description }}</p>

                  <!-- Amenities -->
                  <div class="hotel-amenities">
                    <h5 class="amenities-title">
                      <svg class="amenities-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"></path>
                      </svg>
                      Key Amenities
                    </h5>
                    <div class="amenities-list">
                      <span v-for="amenity in hotel.quickAmenities" :key="amenity" class="amenity-tag">
                        {{ amenity }}
                      </span>
                    </div>
                  </div>

                  <!-- Contact Information -->
                  <div class="hotel-contact">
                    <div class="contact-row">
                      <div class="contact-item">
                        <svg class="contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                          <polyline points="22,6 12,13 2,6"></polyline>
                        </svg>
                        <a :href="'mailto:' + hotel.email" class="contact-link">{{ hotel.email }}</a>
                      </div>
                      <div class="contact-item">
                        <svg class="contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                        </svg>
                        <a :href="'tel:' + hotel.mobile" class="contact-link">{{ hotel.mobile }}</a>
                      </div>
                    </div>
                  </div>

                  <!-- Action Buttons -->
                  <div class="hotel-actions">
                    <a :href="hotel.website" target="_blank" class="btn-website">
                      <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                      </svg>
                      Visit Website
                    </a>
                    <a :href="'mailto:' + hotel.email" class="btn-contact">
                      <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                      Contact Hotel
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Hotels Statistics -->
        <div class="hotels-stats mt-5" data-aos="fade-up">
          <div class="row">
            <div class="col-md-3 col-6 text-center mb-4">
              <div class="stat-card">
                <div class="stat-icon luxury-stat">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5C5.8 4 6 4.2 6 4.5V6h6V4.5c0-.3.2-.5.5-.5h1.5a2.5 2.5 0 0 1 0 5H12v2h6l3 7H3l3-7h2V9z"></path>
                    <path d="m12 2 4 4-4 4-4-4 4-4z"></path>
                  </svg>
                </div>
                <h4 class="stat-number">{{ luxuryCount }}</h4>
                <p class="stat-label">Luxury Hotels (5★)</p>
              </div>
            </div>
            <div class="col-md-3 col-6 text-center mb-4">
              <div class="stat-card">
                <div class="stat-icon business-stat">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14,2 14,8 20,8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10,9 9,9 8,9"></polyline>
                  </svg>
                </div>
                <h4 class="stat-number">{{ businessCount }}</h4>
                <p class="stat-label">Business Hotels (4★)</p>
              </div>
            </div>
            <div class="col-md-3 col-6 text-center mb-4">
              <div class="stat-card">
                <div class="stat-icon comfort-stat">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M7 7h10v10H7z"></path>
                    <path d="M5 10v10h14V10"></path>
                    <path d="m15 7-5-5-5 5"></path>
                  </svg>
                </div>
                <h4 class="stat-number">{{ comfortCount }}</h4>
                <p class="stat-label">Comfort Hotels (3★)</p>
              </div>
            </div>
            <div class="col-md-3 col-6 text-center mb-4">
              <div class="stat-card">
                <div class="stat-icon total-stat">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
                    <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
                    <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
                    <path d="M10 6h4"></path>
                    <path d="M10 10h4"></path>
                    <path d="M10 14h4"></path>
                    <path d="M10 18h4"></path>
                  </svg>
                </div>
                <h4 class="stat-number">{{ totalHotels }}</h4>
                <p class="stat-label">Total Properties</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Special Offers Section -->
    <section class="offers-section">
      <div class="container">
        <h3 class="section-title" data-aos="fade-up">Exclusive Summit Packages</h3>
        <div class="offers-grid">
          <div class="offer-card" data-aos="fade-up" data-aos-delay="100">
            <div class="offer-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <polyline points="16,11 18,13 22,9"></polyline>
              </svg>
            </div>
            <h4>Early Bird Special</h4>
            <p>May 2026 Special Offer — Save 10% when you book early</p>
          </div>

          <div class="offer-card" data-aos="fade-up" data-aos-delay="200">
            <div class="offer-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="m22 21-3-3"></path>
                <circle cx="19" cy="16" r="3"></circle>
              </svg>
            </div>
            <h4>Group Special Rate</h4>
            <p>10% discount for 10–30 delegates &amp; 15% discount for groups above 30</p>
          </div>

          <div class="offer-card" data-aos="fade-up" data-aos-delay="300">
            <div class="offer-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18 10V6c0-2-2-4-4-4H4c-2 0-4 2-4 4v10c0 1.1.9 2 2 2h2"></path>
                <circle cx="7" cy="17" r="2"></circle>
                <circle cx="19" cy="17" r="2"></circle>
              </svg>
            </div>
            <h4>Airport Transfer</h4>
            <p>Complimentary airport pickup and drop-off service with select hotel packages</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Call to Action -->
    <section class="accommodation-cta">
      <div class="container">
        <div class="cta-content" data-aos="zoom-in">
          <h3>Need Assistance with Booking?</h3>
          <p>Our accommodation specialists are here to help you find the perfect stay for your summit experience.</p>
          <div class="cta-buttons">
            <a class="btn-primary" href="mailto:fazal.shaikh@fcmtravel.co.tz">
              <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <path d="m22 6-10 7L2 6"></path>
              </svg>
              fazal.shaikh@fcmtravel.co.tz
            </a>
            <a class="btn-secondary" href="tel:+255677038853">
              <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
              +255 677 038 853
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "AccommodationView",
  data() {
    return {
      activeFilter: 'all',
      hotels: [
        {
          id: 1,
          name: "Mount Meru Hotel",
          email: "reservations@mountmeruhotel.co.tz",
          mobile: "+255 784 108200",
          website: "https://www.mountmeruhotel.co.tz/",
          category: "business",
          categoryLabel: "5★ Luxury",
          image: require("@/assets/images/hotels/mount meru hotel.jpg"),
          location: "Arusha, Tanzania",
          description: "A luxurious 5-star hotel in Arusha, offering 178 rooms with views of Mount Meru or the on-site golf course. The hotel provides a resort-like atmosphere with multiple restaurants, bars, an outdoor pool, and a spa. It also features extensive conference facilities and is conveniently located near Arusha Airport and Kilimanjaro International Airport.",
          quickAmenities: ["WiFi", "Pool", "Spa", "Restaurant"]
        },
        {
          id: 2,
          name: "Grand Melia Arusha",
          email: "gran.melia.arusha@melia.com",
          mobile: "+255 746 982 400",
          website: "https://www.melia.com/en/hotels/tanzania/arusha/gran-melia-arusha",
          category: "luxury",
          categoryLabel: "5★ Luxury",
          image: require("@/assets/images/hotels/Grand Melia Arusha.jpg"),
          location: "Arusha City Center",
          description: "This 5-star hotel is nestled within 18 acres of landscaped coffee and tea plantations, offering elegant accommodations with views of Mount Meru. It features an infinity pool, a wellness spa, and several dining options, including a rooftop cocktail bar. The hotel's tranquil environment is ideal for those seeking relaxation and sophistication.",
          quickAmenities: ["WiFi", "Pool", "Gym", "Conference"]
        },
        {
          id: 3,
          name: "Kibo Palace Hotel",
          email: "reservations@kibopalacehotel.com",
          mobile: "+255 733 936 444",
          website: "https://kibopalacehotel.com/",
          category: "luxury",
          categoryLabel: "5★ Luxury",
          image: require("@/assets/images/hotels/Kibo Palace Hotel.jpg"),
          location: "Moshi Town",
          description: "A 4-star hotel in a serene environment just outside of Arusha's city center, known for its landscaped gardens and large outdoor pool. The hotel has 77 well-appointed rooms, a restaurant serving African and international cuisine, and a spa and wellness center.",
          quickAmenities: ["WiFi", "Business Center", "Restaurant"]
        },
        {
          id: 4,
          name: "Four Points By Sheraton Arusha",
          email: "info@ourpointsthearushahotel.com",
          mobile: "+255 27 2917777",
          website: "https://www.marriott.com/en-us/hotels/jrofp-four-points-arusha-the-arusha-hotel/overview/",
          category: "business",
          categoryLabel: "4★ Business",
          image: require("@/assets/images/hotels/Four Points By Sheraton Arusha.jpg"),
          location: "Arusha Business District",
          description: "This historic 4-star hotel, originally built in 1894, is located at the Clocktower Roundabout in the heart of Arusha. It combines colonial architecture with modern amenities, including a full-service restaurant, the famous Hatari Bar, an outdoor pool, and a 24-hour gym.",
          quickAmenities: ["WiFi", "Fitness", "Meeting Rooms"]
        },
        {
          id: 5,
          name: "Gold Crest Hotel - Arusha",
          email: "info@goldcresthotel.com",
          mobile: "+255 677 014 958",
          website: "https://www.goldcresthotel.com/arusha/",
          category: "business",
          categoryLabel: "4★ Business",
          image: require("@/assets/images/hotels/Gold Crest.jpg"),
          location: "Arusha Central",
          description: "A 4-star hotel situated about a kilometer from Arusha's city center, offering spacious suites with separate seating areas and kitchenettes. The hotel is well-regarded for its clean, comfortable rooms and friendly staff, and features a restaurant, bar, and outdoor swimming pool.",
          quickAmenities: ["WiFi", "Restaurant", "Parking"]
        },
        {
          id: 6,
          name: "Parrot Hotel Arusha",
          email: "reservations@parrothotelarusha.com",
          mobile: "+255 748 504 600",
          website: "https://parrothotelarusha.com/",
          category: "business",
          categoryLabel: "4★ Business",
          image: require("@/assets/images/hotels/Parrot Hotel Arusha.webp"),
          location: "Arusha Town",
          description: "A 4-star hotel located near the Uhuru monument, providing comfortable, modern, and clean rooms with private bathrooms and city views. Amenities include a spa, hot tub, fitness center, and a family-friendly restaurant offering a variety of cuisines.",
          quickAmenities: ["WiFi", "Restaurant", "Garden"]
        },
        {
          id: 7,
          name: "New Njiro Legacy",
          email: "info@njirolegacy.com",
          mobile: "+255 677 234 754",
          website: "https://www.njirolegacy.com/",
          category: "business",
          categoryLabel: "4★ Business",
          image: require("@/assets/images/hotels/New Njiro Legacy.jpg"),
          location: "Njiro, Arusha",
          description: "Situated in the Njiro area of Arusha, this modern hotel offers comfortable rooms, conference facilities, and a year-round outdoor swimming pool. It is known for its exceptionally friendly staff and is a good option for travelers starting or ending a safari.",
          quickAmenities: ["WiFi", "Garden", "Parking"]
        },
        {
          id: 8,
          name: "Seven Skies Hotel",
          email: "reservations@sevenskieshotel.com",
          mobile: "+255 736 663 366",
          website: "https://sevenskieshotel.com/?v=6",
          category: "luxury",
          categoryLabel: "5★ Luxury",
          image: require("@/assets/images/hotels/Seven Skies Hotel.jpg"),
          location: "Arusha",
          description: "Located in the heart of Arusha, this hotel offers spacious, sound-proofed rooms, some with kitchenettes, and a penthouse with views of the city. It features a restaurant, free Wi-Fi, a rooftop terrace, and a convenient location close to many of Arusha's main attractions.",
          quickAmenities: ["WiFi", "Conference", "Restaurant"]
        },
        {
          id: 9,
          name: "Lush Garden Hotel",
          email: "reservation@lushgardenhotels.com",
          mobile: "+255 787 884 466",
          website: "https://lushgardenhotels.co.tz/",
          category: "comfort",
          categoryLabel: "3★ Comfort",
          image: require("@/assets/images/hotels/Lush Garden Hotel.jpg"),
          location: "Arusha Gardens",
          description: "An affordable 2-star hotel located just outside Arusha town, with a large, well-maintained garden. The hotel offers over 100 air-conditioned rooms and includes amenities like a swimming pool, conference facilities, and a multi-cuisine restaurant.",
          quickAmenities: ["WiFi", "Garden", "Restaurant"]
        },
        {
          id: 10,
          name: "Moshono Hillside",
          email: "reservation@moshonohillside.com",
          mobile: "+255 748 002 429",
          website: "https://moshonohillside.com/",
          category: "business",
          categoryLabel: "3★ Business",
          image: require("@/assets/images/hotels/Moshono Hillside.jpg"),
          location: "Moshi Hillside",
          description: "This hotel, situated in the serene hills outside of Arusha, provides comfortable family rooms with private bathrooms and modern amenities. Guests can enjoy leisure facilities like a spa, a fitness center, a swimming pool, and a waterpark, along with impressive views from the hilltop.",
          quickAmenities: ["WiFi", "Spa", "Pool", "Views"]
        },
        {
          id: 11,
          name: "African Tulip Hotel",
          email: "res@theafricantulip.co.tz",
          mobile: "+255 699 799 873",
          website: "https://theafricantulip.co.tz/",
          category: "business",
          categoryLabel: "4★ Business",
          image: require("@/assets/images/hotels/African Tulip Hotel.jpg"),
          location: "Dar es Salaam",
          description: "An upmarket 4-star boutique hotel with safari-themed decor, situated in a convenient location near Arusha's business district. It offers refined rooms, a restaurant serving African and international cuisine, and an outdoor swimming pool.",
          quickAmenities: ["WiFi", "Pool", "Spa", "Fine Dining"]
        },
        {
          id: 12,
          name: "Palace Hotel Arusha",
          email: "reservations@palacehotelarusha.com",
          mobile: "+255 755 706 206",
          website: "https://palacehotelarusha.com/",
          category: "business",
          categoryLabel: "4★ Business",
          image: require("@/assets/images/hotels/Palace Hotel Arusha.jpg"),
          location: "Arusha Central",
          description: "A centrally located 4-star hotel known for its refined lodging, delicious buffet, and meeting facilities. It features clean rooms, a spa, a fitness center, a restaurant, a coffee shop, and a bar.",
          quickAmenities: ["WiFi", "Business Center", "Restaurant"]
        },
        {
          id: 13,
          name: "Arusha Corridor Springs Hotel",
          email: "info@corridorspringshotel.com",
          mobile: "+255 787 545 075",
          website: "https://corridorspringshotel.com/",
          category: "business",
          categoryLabel: "4★ Business",
          image: require("@/assets/images/hotels/Arusha Corridor Springs Hotel.jpg"),
          location: "Arusha Corridor",
          description: "Located a short walk from Arusha's town center, this 3-star hotel features an outdoor pool, a restaurant, and conference facilities. It offers well-appointed rooms and is known for its garden and serene atmosphere.",
          quickAmenities: ["WiFi", "Conference", "Restaurant"]
        },
        {
          id: 14,
          name: "Silver Palm Hotel",
          email: "info@silverpalmhotel.com",
          mobile: "+255 767 286 630",
          website: "https://www.silverpalmhotel.com",
          category: "comfort",
          categoryLabel: "3★ Comfort",
          image: require("@/assets/images/hotels/Silver Palm Hotel.jpg"),
          location: "Arusha",
          description: "A centrally located 3-star hotel in Arusha, offering 50 well-appointed rooms with a contemporary design. It provides guests with a restaurant, a swimming pool, and easy access to the city's main attractions.",
          quickAmenities: ["WiFi", "Restaurant", "Garden"]
        },
        {
          id: 15,
          name: "New Safari Hotel",
          email: "reservation@newsafarihotel.com",
          mobile: "+255 787 326 122",
          website: "https://newsafarihotel.com-tanzania.com/",
          category: "business",
          categoryLabel: "4★ Business",
          image: require("@/assets/images/hotels/New Safari Hotel.jpg"),
          location: "Arusha Safari District",
          description: "A budget-friendly hotel with a central location in Arusha, known for its good service and comfortable, modern rooms. It features a restaurant serving a mix of African, Asian, and Italian cuisines, a lounge, and a coffee shop.",
          quickAmenities: ["WiFi", "Safari Tours", "Restaurant"]
        },
        {
          id: 16,
          name: "Kika Resort Hotel",
          email: "info@kikaresorthotel.com",
          mobile: "+255 748 270 605",
          website: "https://kikaresorthotel.com/",
          category: "comfort",
          categoryLabel: "3★ Comfort",
          image: require("@/assets/images/hotels/kika-resort-hotel.jpg"),
          location: "Arusha Central",
          description: "A contemporary, mid-range hotel located near the heart of Arusha, featuring a minimalist design and a peaceful atmosphere. It is known for its modern, clean interiors and views of Mount Meru, offering a serene stay within walking distance of the city's main landmarks and markets.",
          quickAmenities: ["WiFi", "Restaurant", "Parking"]
        },
        {
          id: 17,
          name: "Senator Hotel",
          email: "info@senatorhotel.co.tz",
          mobile: "+255 789 746 464",
          website: "https://www.senatorhotel.co.tz/index.html",
          category: "business",
          categoryLabel: "3★ Business",
          image: require("@/assets/images/hotels/Senator Hotel.jpg"),
          location: "Arusha Business Center",
          description: "Located in Arusha, this 3-star hotel offers 34 comfortable rooms and a diverse restaurant, Simanjiro Restaurant. It features amenities like complimentary Wi-Fi, secure parking, airport transfers, and laundry services.",
          quickAmenities: ["WiFi", "Business Center", "Meeting Rooms"]
        },
        {
          id: 18,
          name: "Point Zone Resort",
          email: "info@pointzoneresort.co.tz",
          mobile: "+255 767 300 222",
          website: "#",
          category: "comfort",
          categoryLabel: "3★ Comfort",
          image: require("@/assets/images/hotels/point zone hotel.jpg"),
          location: "Arusha Central (Mianzini)",
          description: "A distinctive 3-star hotel located in the heart of Arusha, popular as a hideaway for both business travelers and safari guests. The property offers a contemporary Tanzanian style with views of Mount Meru and is well-regarded for its spacious, clean rooms and affordable pricing. It features an on-site restaurant, a bar/lounge with a winery feel, and extensive conference facilities.",
          quickAmenities: ["WiFi", "Restaurant & Bar", "Free Parking"]
        }
      ]
    };
  },
  computed: {
    filteredHotels() {
      if (this.activeFilter === 'all') {
        return this.hotels;
      }
      return this.hotels.filter(hotel => hotel.category === this.activeFilter);
    },
    luxuryCount() {
      return this.hotels.filter(hotel => hotel.category === 'luxury').length;
    },
    businessCount() {
      return this.hotels.filter(hotel => hotel.category === 'business').length;
    },
    comfortCount() {
      return this.hotels.filter(hotel => hotel.category === 'comfort').length;
    },
    totalHotels() {
      return this.hotels.length;
    }
  },
  methods: {
    filterHotels(category) {
      this.activeFilter = category;
    }
  }
};
</script>

<style scoped>
/* Accommodation Page Styles */
.accommodation-page {
  min-height: 100vh;
}

/* Hero Section */
.accommodation-hero-section {
  position: relative;
  height: 70vh;
  min-height: 500px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.6) contrast(1.1);
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.5) 0%,
    rgba(0, 0, 0, 0.3) 50%,
    rgba(34, 139, 34, 0.4) 100%
  );
}

.hero-content {
  position: relative;
  z-index: 10;
  text-align: center;
  color: white;
  width: 100%;
}

.hero-title {
  font-size: 4rem;
  font-weight: 900;
  margin-bottom: 20px;
  text-shadow: 0 0 30px rgba(0, 0, 0, 0.5);
  background: linear-gradient(
    45deg,
    #ffffff,
    var(--color-primary-light),
    #ffffff
  );
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: textShimmer 4s ease-in-out infinite;
}

@keyframes textShimmer {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.hero-subtitle {
  font-size: 1.8rem;
  margin-bottom: 20px;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
}

.hero-description {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  max-width: 600px;
  margin: 0 auto;
  text-shadow: 0 0 15px rgba(0, 0, 0, 0.5);
}

/* Filter Section */
.filter-section {
  padding: 60px 0 40px;
  background: var(--color-white);
}

.filter-controls {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

.filter-btn {
  background: var(--color-white);
  color: var(--color-primary-dark);
  border: 2px solid var(--color-primary);
  padding: 12px 24px;
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.filter-btn:hover {
  background: var(--color-primary-light);
  color: white;
  transform: translateY(-2px);
}

.filter-btn.active {
  background: var(--color-primary);
  color: white;
  box-shadow: var(--shadow-lg);
}

/* Hotels Section */
.hotels-section {
  padding: 60px 0 80px;
  background: var(--color-background-light);
}

/* Section Introduction */
.section-intro .section-title {
  font-size: 2.8rem;
  color: var(--color-primary-dark);
  margin-bottom: 20px;
  font-weight: bold;
}

.section-intro .section-description {
  font-size: 1.2rem;
  color: var(--color-text-secondary);
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.8;
}

/* Hotels Showcase Gallery */
.hotels-showcase-gallery {
  margin-top: 40px;
}

/* Hotel Showcase Cards */
.hotel-showcase-card {
  background: var(--color-white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.hotel-showcase-card:hover {
  transform: translateY(-8px);
  box-shadow: var(--shadow-2xl);
  border-color: var(--color-primary-light);
}

/* Hotel Image Header */
.hotel-image-header {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.hotel-main-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.3s ease;
}

.hotel-showcase-card:hover .hotel-main-image {
  transform: scale(1.05);
}

.hotel-category-overlay {
  position: absolute;
  top: 15px;
  right: 15px;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
}

/* Hotel Card Content */
.hotel-card-content {
  padding: 25px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.hotel-header {
  margin-bottom: 15px;
}

.hotel-title {
  font-size: 1.5rem;
  color: var(--color-primary-dark);
  margin-bottom: 8px;
  font-weight: bold;
  line-height: 1.3;
}

.hotel-location {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--color-text-secondary);
  font-size: 0.95rem;
}

.location-icon {
  width: 16px;
  height: 16px;
  color: var(--color-primary);
}

.hotel-description {
  color: var(--color-text-primary);
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 20px;
  flex: 1;
}

/* Hotel Amenities Section */
.hotel-amenities {
  margin-bottom: 20px;
}

.amenities-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  color: var(--color-primary-dark);
  margin-bottom: 12px;
  font-weight: 600;
}

.amenities-icon {
  width: 16px;
  height: 16px;
  color: var(--color-primary);
}

.amenities-list {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.amenity-tag {
  background: var(--color-background-light);
  color: var(--color-primary-dark);
  padding: 4px 10px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid var(--color-primary-light);
}

/* Hotel Contact Section */
.hotel-contact {
  margin-bottom: 20px;
  padding: 15px;
  background: var(--gradient-section);
  border-radius: var(--radius-lg);
}

.contact-row {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.contact-icon {
  width: 16px;
  height: 16px;
  color: var(--color-primary);
  flex-shrink: 0;
}

.contact-link {
  color: var(--color-primary-dark);
  text-decoration: none;
  font-size: 0.9rem;
  word-break: break-all;
  transition: all 0.3s ease;
}

.contact-link:hover {
  color: var(--color-primary-light);
  text-decoration: underline;
}

/* Hotel Actions */
.hotel-actions {
  display: flex;
  gap: 10px;
  margin-top: auto;
}

.btn-website,
.btn-contact {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: var(--radius-lg);
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
}

.btn-website {
  background: var(--color-primary);
  color: white;
}

.btn-website:hover {
  background: var(--color-primary-light);
  color: white;
  text-decoration: none;
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.btn-contact {
  background: var(--color-success);
  color: white;
}

.btn-contact:hover {
  background: var(--color-success-dark);
  color: white;
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
  text-decoration: none;
}

.btn-icon {
  width: 16px;
  height: 16px;
}

/* Category Badge Styles */
.category-badge {
  background: var(--color-primary);
  color: white;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
}

.category-badge.luxury {
  background: linear-gradient(135deg, #d4af37, #ffd700);
  color: #1a1a1a;
}

.category-badge.business {
  background: var(--color-primary);
}

.category-badge.comfort {
  background: var(--color-success);
}

/* Responsive Design for Hotel Cards */
@media (max-width: 768px) {
  .hotels-showcase-gallery .col-lg-6 {
    margin-bottom: 1.5rem;
  }

  .hotel-showcase-card {
    margin-bottom: 0;
  }

  .hotel-card-content {
    padding: 20px;
  }

  .hotel-title {
    font-size: 1.3rem;
  }

  .hotel-actions {
    flex-direction: column;
  }

  .btn-website,
  .btn-contact {
    width: 100%;
  }

  .contact-row {
    gap: 8px;
  }

  .contact-link {
    font-size: 0.85rem;
  }
}

/* Hotels Statistics Section */
.hotels-stats {
  background: var(--color-white);
  border-radius: var(--radius-xl);
  padding: 40px;
  box-shadow: var(--shadow-md);
}

.stat-card {
  background: linear-gradient(135deg, var(--color-background-light), var(--color-white));
  padding: 30px 20px;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: var(--shadow-lg);
  border-color: var(--color-primary-light);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  font-size: 1.5rem;
}

.luxury-stat {
  background: linear-gradient(135deg, #d4af37, #ffd700);
  color: #1a1a1a;
}

.business-stat {
  background: linear-gradient(135deg, var(--color-primary), var(--color-primary-light));
  color: white;
}

.comfort-stat {
  background: linear-gradient(135deg, var(--color-success), var(--color-success-dark));
  color: white;
}

.total-stat {
  background: linear-gradient(135deg, var(--color-primary-dark), var(--color-primary));
  color: white;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: bold;
  color: var(--color-primary-dark);
  margin-bottom: 5px;
}

.stat-label {
  color: var(--color-text-secondary);
  font-size: 0.95rem;
  margin: 0;
}

/* Offers Section */
.offers-section {
  padding: 80px 0;
  background: var(--color-white);
}

.section-title {
  font-size: 2.8rem;
  color: var(--color-primary-dark);
  text-align: center;
  margin-bottom: 50px;
  font-weight: bold;
}

.offers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-top: 20px;
}

.offer-card {
  background: var(--color-background-light);
  padding: 40px 30px;
  border-radius: var(--radius-xl);
  text-align: center;
  box-shadow: var(--shadow-md);
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.offer-card:hover {
  border-color: var(--color-primary-light);
  transform: translateY(-5px);
  box-shadow: var(--shadow-lg);
}

.offer-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  padding: 20px;
  background: var(--color-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.offer-icon svg {
  width: 40px;
  height: 40px;
  color: white;
}

.offer-card h4 {
  font-size: 1.5rem;
  color: var(--color-primary-dark);
  margin-bottom: 15px;
  font-weight: bold;
}

.offer-card p {
  color: var(--color-text-primary);
  line-height: 1.6;
}

/* Call to Action */
.accommodation-cta {
  padding: 80px 0;
  background: linear-gradient(
    135deg,
    var(--color-primary-dark) 0%,
    var(--color-primary) 100%
  );
  color: white;
}

.cta-content {
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
}

.cta-content h3 {
  font-size: 2.5rem;
  margin-bottom: 20px;
  font-weight: bold;
}

.cta-content p {
  font-size: 1.2rem;
  margin-bottom: 40px;
  opacity: 0.9;
}

.cta-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

.btn-primary {
  background: linear-gradient(
    135deg,
    var(--color-success),
    var(--color-primary-light)
  );
  color: white;
  border: none;
  padding: 18px 40px;
  font-size: 1.2rem;
  font-weight: bold;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  text-transform: none;
  letter-spacing: 0.3px;
  text-decoration: none;
  font-size: 1.05rem;
}

.btn-primary:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 0 30px rgba(255, 255, 255, 0.3);
}

.btn-secondary {
  background: transparent;
  color: white;
  border: 2px solid white;
  padding: 18px 40px;
  font-size: 1.2rem;
  font-weight: bold;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  text-transform: none;
  letter-spacing: 0.3px;
  text-decoration: none;
  font-size: 1.05rem;
}

.btn-secondary:hover {
  background: white;
  color: var(--color-primary-dark);
  transform: translateY(-3px) scale(1.05);
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.4rem;
  }

  .hero-description {
    font-size: 1rem;
    padding: 0 20px;
  }

  /* Hide table header on mobile and stack content vertically */
  .table-header {
    display: none;
  }

  .hotel-row {
    display: block;
    border: 1px solid var(--color-primary);
    border-radius: var(--radius-lg);
    margin-bottom: 20px;
    background: var(--color-white);
    padding: 0;
  }

  .row-cell {
    display: block;
    padding: 15px 20px;
    border-bottom: 1px solid #eee;
    text-align: left;
  }

  .row-cell:last-child {
    border-bottom: none;
  }

  .row-cell:before {
    content: attr(data-label);
    font-weight: bold;
    color: var(--color-primary);
    display: block;
    margin-bottom: 8px;
    text-transform: uppercase;
    font-size: 0.8rem;
  }

  .row-no:before { content: "Number: "; }
  .row-hotel:before { content: "Hotel: "; }
  .row-image:before { content: "Image: "; }
  .row-email:before { content: "Email: "; }
  .row-mobile:before { content: "Mobile: "; }
  .row-actions:before { content: "Actions: "; }

  .hotel-image-container {
    width: 120px;
    height: 80px;
  }

  .filter-controls {
    flex-direction: column;
    align-items: center;
  }

  .filter-btn {
    width: 200px;
  }

  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }

  .btn-primary,
  .btn-secondary {
    width: 250px;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 2rem;
  }

  .hero-subtitle {
    font-size: 1.2rem;
  }

  .hotel-content {
    padding: 20px;
  }

  .offers-grid {
    grid-template-columns: 1fr;
  }

  .cta-content h3 {
    font-size: 2rem;
  }
}
</style>